import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import { cn } from '@/lib/utils';

/**
 * Password field with a show/hide toggle. Drop-in replacement for
 * <Input type="password" />: it brings its own relative wrapper, so it also
 * works inside the existing wrappers that position a Lock icon on the left.
 */
const PasswordInput = React.forwardRef(({ className, ...props }, ref) => {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        // Room for the toggle so a long password doesn't run under it.
        className={cn('pr-9', className)}
        {...props}
      />
      <button
        // Load-bearing: these fields sit inside <form>s, and a button with no
        // type defaults to submit - revealing the password would submit the form.
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t('auth.hide_password') : t('auth.show_password')}
        aria-pressed={visible}
        className="absolute right-0 top-0 h-full px-2.5 flex items-center text-gray-500 hover:text-gray-300 transition-colors focus-visible:outline-none focus-visible:text-gray-300"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
